import { KeyboardArrowRight } from '@mui/icons-material';
import { Box, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { useRef } from 'react';
import { TRACER_CONSTANTS } from '../../common/styles/constants';
import { CardList } from '../../store/CardList';
import { CardNode } from '../../store/CardNode';
import { TracerStore } from '../../store/TracerStore';

type Props = {
  list: CardList;
  ruleNumber: number;
};

// Overall there are 7 possible states that the 3 card collection can be in
// 1. Just "high" on -> [200,200,200] (indicated by all values being equal)
// 2. Just "med" on -> [0,200,200] (indicated by a leading "0" and values[1] === values[2])
// 3. Just "low" on -> [0,0,200] (indicated by values[0] === values[1] === 0 and last number is valid)
// 4. "high" + "med" on -> [200,300,300] (indicated by values[1]==values[2])
// 5. "high" + "low" on -> [200,200,300] (indicated by values[0]==values[1])
// 6. "med" + "low" on -> [0,200,300] (indicated by leading 0 and different med & low vals)
// 7. all are on -> [100,200,300] (3 unique non-zero values)

type CardTitleProps = {
  color: string;
  borderColor: string;
  title: string;
};

const CardTitle = ({ color, borderColor, title }: CardTitleProps) => {
  return (
    <Typography
      variant="body2"
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'start',
        '&:before': {
          content: '""',
          display: 'inline-block',
          height: '1rem',
          width: '1rem',
          backgroundColor: color,
          border: `2px solid ${borderColor}`,
          borderRadius: '50%',
          mr: 1,
        },
      }}
    >
      {title}
    </Typography>
  );
};

type CardProps = {
  card: CardNode;
  ruleNumber: number;
};

const FromComponent = ({ card, ruleNumber }: CardProps) => {
  const store = useRef(TracerStore.getInstance()).current;
  const unit = card.getUnit();
  const label = t(`createProfile.units.${unit}.from`);
  const isBitOrder = unit === 'bits';
  const fromValue = card.getFromValue();
  const isEditable = store.isEditable();
  const value = card.getDisplayValue();
  const id = card.getId();
  const cardOnChangeValue = card.onChangeValue.bind(card);
  const onChangeValue = (value: string | number) => {
    cardOnChangeValue(value);
    store.validateForm();
  };

  // If the card is not editable or in Bit Order
  // we display a disabled TextField
  if (!isEditable || isBitOrder) {
    return (
      <TextField
        variant="standard"
        label={label}
        value={isBitOrder ? fromValue : value}
        data-role={`tracer-rule-${ruleNumber}-card-${id}-from-value`}
        disabled
      />
    );
  }

  // If the card is editable and in Day Order
  // we display an enabled TextField
  return (
    <TextField
      fullWidth
      inputMode="numeric"
      className="tracer-input-field"
      type="text"
      label={label}
      variant="standard"
      value={value}
      error={value <= fromValue}
      helperText={value <= fromValue ? t('validators.operational.compare.gt', { value: fromValue }) : ''}
      onChange={(event) => onChangeValue(event.target.value)}
      inputProps={{ min: 1, step: 1, pattern: '[1-9][0-9]*' }}
      data-role={`tracer-rule-${ruleNumber}-card-${id}-from-value`}
    />
  );
};

const ToComponent = ({ card, ruleNumber }: CardProps) => {
  const store = useRef(TracerStore.getInstance()).current;
  const unit = card.getUnit();
  const label = t(`createProfile.units.${unit}.to`);
  const isBitOrder = unit === 'bits';
  const fromValue = card.getFromValue();
  const toValue = card.getToValue();
  const isEditable = store.isEditable();
  const value = card.getDisplayValue();
  const id = card.getId();
  const cardOnChangeValue = card.onChangeValue.bind(card);
  const onChangeValue = (value: string | number) => {
    cardOnChangeValue(value);
    store.validateForm();
  };

  // If the card is not editable or in Day Order we
  // display a disabled TextField
  if (!isEditable || !isBitOrder) {
    return (
      <TextField variant="standard" label={label} value={isBitOrder ? value : toValue} data-role={`tracer-rule-${ruleNumber}-card-${id}-to-value`} disabled />
    );
  }
  // If the card is editable and in Bit Order
  // we display an enabled TextField
  return (
    <TextField
      fullWidth
      inputMode="numeric"
      className="tracer-input-field"
      type="text"
      label={label}
      variant="standard"
      value={value}
      error={value < fromValue}
      helperText={value < fromValue ? t('validators.operational.compare.gte', { value: fromValue }) : ''}
      onChange={(event) => onChangeValue(event.target.value)}
      inputProps={{ min: 1, step: 1, pattern: '[1-9][0-9]*' }}
      data-role={`tracer-rule-${ruleNumber}-card-${id}-to-value`}
    />
  );
};

const Card = ({ card, ruleNumber }: CardProps) => {
  const store = useRef(TracerStore.getInstance()).current;

  const unit = card.getUnit();
  const id = card.getId();
  const isEditable = store.isEditable();
  const title = card.getTitle();
  const defaultSetting = card.getDefaultSettingString();
  const color = card.getColor();
  const fromValue = card.getFromValue();
  const disableSwitch = card.getDisableSwitch();
  const value = card.getDisplayValue();
  const isEnabled = card.isEnabled();
  const borderColor = card.getBorderColor();
  const cardToggleEnable = card.toggleEnable.bind(card);

  const toggleEnable = (check: boolean) => {
    cardToggleEnable(check);
    store.validateForm();
  };

  const ariaLabel = isEnabled
    ? t('a11y.profile.thresholds.toggleCurrentValue', {
        threshold: title.toLocaleLowerCase(),
        from: fromValue,
        to: value,
        unit: unit,
      })
    : t('a11y.profile.thresholds.toggleCurrentValueNotSet', { threshold: title.toLocaleLowerCase() });

  return (
    <Stack direction="column" sx={{ width: 1, minHeight: TRACER_CONSTANTS.THRESHOLD_MIN_HEIGHT }}>
      <Stack component={Paper} sx={{ p: 2, flex: 1 }}>
        {isEditable && (
          <FormControlLabel
            data-role={`tracer-rule-${ruleNumber}-card-${id}-toggle`}
            labelPlacement="start"
            sx={{ justifyContent: 'space-between', width: 1, ml: 0 }}
            control={
              <Switch
                checked={isEnabled}
                size="small"
                onChange={(_, check) => toggleEnable(check)}
                disabled={!isEditable || disableSwitch}
                inputProps={{ 'aria-label': ariaLabel }}
              />
            }
            label={
              <span aria-hidden>
                <CardTitle color={color} borderColor={borderColor} title={title} />
              </span>
            }
          />
        )}
        {!isEditable && <CardTitle color={color} borderColor={borderColor} title={title} />}
        {isEnabled && (
          <Stack direction="row" sx={{ pt: 2, alignItems: 'flex-start', justifyContent: 'space-between', flex: 1, gap: 1 }}>
            <FromComponent ruleNumber={ruleNumber} card={card} />
            <KeyboardArrowRight sx={{ color: 'grey.700', marginTop: 3 }} />
            <ToComponent ruleNumber={ruleNumber} card={card} />
          </Stack>
        )}
        {!isEnabled && (
          <Stack direction="row" sx={{ pt: 2, alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1 }}>
            <Typography>{t('createProfile.thresholds.notSet', { threshold: title.toLocaleLowerCase() })}</Typography>
          </Stack>
        )}
      </Stack>
      {isEditable && (
        <Typography variant="body2" sx={{ color: 'text.secondary', paddingLeft: 1, fontStyle: 'italic', paddingTop: 1 }}>
          {t('createProfile.defaultSetting', { setting: defaultSetting })}
        </Typography>
      )}
    </Stack>
  );
};

export const ThresholdDisplay = ({ list, ruleNumber }: Props) => {
  const shortDescription = list.getDescription();

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={4} sx={{ p: 2, backgroundColor: 'grey.100' }}>
      <Typography sx={{ flex: 4 }} variant="body2">
        {shortDescription}
      </Typography>

      <Box sx={{ width: 1, flex: 6 }}>
        <Stack direction="row" gap={1} sx={{ width: 1 }}>
          <Card card={list.getFirst()} ruleNumber={ruleNumber} />
          <Card card={list.getMiddle()} ruleNumber={ruleNumber} />
          <Card card={list.getLast()} ruleNumber={ruleNumber} />
        </Stack>
      </Box>
    </Stack>
  );
};
